import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../api/client';
import { fetchAuthStatus } from '../auth/status';
import { APP_VIEWPORT_SX, PageLayout } from '../layout/primitives';

const ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'Your email is not authorized to access this application.',
  token_exchange_failed: 'Failed to authenticate with Google. Please try again.',
  userinfo_failed: 'Failed to retrieve your account information from Google.',
  email_not_verified: 'Your Google email address is not verified.',
  access_denied: 'Access was denied. Please try again.',
  invalid_state: 'Your sign-in session expired before Google returned. Please try again.',
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [loginAvailable, setLoginAvailable] = useState(true);
  const reconnectMode = React.useMemo(
    () => new URLSearchParams(location.search).get('reconnect') === '1',
    [location.search]
  );

  useEffect(() => {
    if (location.pathname === '/login/callback') {
      const hash = window.location.hash;
      const tokenMatch = hash.match(/token=([^&]+)/);
      if (tokenMatch) {
        setProcessing(true);
        apiClient.setAuthToken(tokenMatch[1]);
        window.history.replaceState(null, '', '/');
        navigate('/', { replace: true });
        return;
      }
    }

    const params = new URLSearchParams(location.search);
    const errorCode = params.get('error');
    if (errorCode) {
      setError(ERROR_MESSAGES[errorCode] || `Authentication error: ${errorCode}`);
    }
  }, [location, navigate]);

  useEffect(() => {
    const syncAuthState = async () => {
      try {
        const status = await fetchAuthStatus();
        if (status.authDisabled) {
          if (!reconnectMode) {
            navigate('/', { replace: true });
          }
          return;
        }

        setLoginAvailable(status.loginAvailable);
        if (!status.loginAvailable) {
          setError(
            'Authentication is not configured on this backend. Set HOME_GOOGLE_CLIENT_ID, HOME_GOOGLE_CLIENT_SECRET, and HOME_JWT_SECRET, or explicitly enable HOME_DISABLE_AUTH_FOR_LOCAL_DEV=true for loopback-only local validation.'
          );
          return;
        }

        const resp = await fetch(apiClient.resolveUrl('/api/auth/me'), {
          credentials: 'include',
          headers: apiClient.getAuthHeaders(),
        });
        if (resp.ok && !reconnectMode) {
          navigate('/', { replace: true });
        }
      } catch {
        // Ignore and stay on the login page.
      }
    };

    void syncAuthState();
  }, [navigate, reconnectMode]);

  if (processing) {
    return (
      <Box sx={[APP_VIEWPORT_SX, { overflow: 'auto' }]}>
        <PageLayout
          fillHeight
          contentMaxWidth={480}
          sx={{ justifyContent: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: { xs: 6, sm: 0 },
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Finishing sign-in…
            </Typography>
          </Box>
        </PageLayout>
      </Box>
    );
  }

  return (
    <Box sx={[APP_VIEWPORT_SX, { overflow: 'auto' }]}>
      <PageLayout
        fillHeight
        contentMaxWidth={480}
        sx={{ justifyContent: { xs: 'center', sm: 'center' } }}
      >
        <Card
          sx={{
            width: '100%',
            borderRadius: { xs: 3, sm: 4 },
            border: { xs: 1, sm: 0 },
            borderColor: 'divider',
            boxShadow: { xs: 'none', sm: undefined },
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: 2.5, sm: 3 },
              px: { xs: 2.5, sm: 4 },
              py: { xs: 3, sm: 4 },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
              }}
            >
              AH Home
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              {reconnectMode ? 'Reconnect Google to refresh widget access' : 'Sign in to continue'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}

            {reconnectMode && !error && (
              <Alert severity="info" sx={{ width: '100%' }}>
                This keeps your AH Home session but runs Google OAuth again so Gmail, Calendar, Notes, and Books can refresh their access.
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!loginAvailable}
              startIcon={<GoogleIcon />}
              onClick={() => { window.location.href = apiClient.resolveUrl('/api/auth/login'); }}
              sx={{ textTransform: 'none', minHeight: 48, py: 1.5 }}
            >
              {reconnectMode ? 'Reconnect Google' : 'Sign in with Google'}
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    </Box>
  );
};

export default LoginPage;
